import React from "react";
import * as utils from "../../../components/utils.js";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import IconSelector from "../../../components/Icon_Selector.js";
import { useChatContext } from "./chat_context.jsx";

export default function ChatScreenInputBox() {
  const { t } = useTranslation("labels");
  const fileListRef = useRef(null);

  // Get all needed state and functions from context
  const {
    history,
    setHistory,
    toAddInTheHistory,
    setToAddInTheHistory,
    setCurrentQuestion,
    isStreaming,
    setIsStreaming,
    isQueryEmpty,
    setIsQueryEmpty,
    fileInfo,
    _sendMessage,
    showSettings,
    setShowSettings,
    showHistorySidebar,
    setShowHistorySidebar,
    showFolderDocuments,
    folderFiles,
    setShowFolderDocuments,
    showTagDropdown,
    setShowTagDropdown,
    selectedIndex,
    setSelectedIndex,
    isFolderFilesLoading,
  } = useChatContext();

  const [tagFilter, setTagFilter] = useState("");
  const [tagFiles, setTagFiles] = useState([]);
  const [textareaElement, setTextareaElement] = useState(null);

  // Scroll the list of files when changing the element selected
  useEffect(() => {
    if (fileListRef.current && showTagDropdown) {
      const selectedElement = fileListRef.current.querySelector(
        `.tag-item:nth-child(${selectedIndex + 1})`
      );
      if (selectedElement) {
        const containerRect = fileListRef.current.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();

        if (elementRect.bottom > containerRect.bottom) {
          selectedElement.scrollIntoView({ block: "end", behavior: "smooth" });
        } else if (elementRect.top < containerRect.top) {
          selectedElement.scrollIntoView({
            block: "start",
            behavior: "smooth",
          });
        }
      }
    }
  }, [selectedIndex, showTagDropdown]);

  const getContextFiles = async () => {
    let files = [];

    if (fileInfo) {
      files.push({
        id: fileInfo.Id,
        name: fileInfo.Name,
        type: fileInfo.MimeType,
      });
    }
    return files;
  };

  const openEditor = (file) => {
    if (file) {
      window.location.href = `/editor/${file.Id}`;
    }
  };


  const handleTextareaInput = async (e) => {
    const textarea = e.target;
    const text = textarea.value;
    const cursorPosition = textarea.selectionStart;

    const textBeforeCursor = text.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf("@");

    if (
      atIndex !== -1 &&
      !textBeforeCursor.substring(atIndex + 1).includes(" ")
    ) {
      setTagFilter(textBeforeCursor.substring(atIndex + 1));
      setSelectedIndex(0);
      setShowTagDropdown(true);

      if (!tagFiles.length) {
        const files = await getContextFiles();
        setTagFiles(files);
      }
    } else {
      setShowTagDropdown(false);
    }

    setIsQueryEmpty(textarea.value.trim() === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isQueryEmpty || isStreaming) return;

    const query = textareaElement?.value.trim();
    if (!query) return;

    // Clear input
    if (textareaElement) {
      textareaElement.value = "";
      setIsQueryEmpty(true);
    }

    // Add user message to history immediately
    if (
      toAddInTheHistory &&
      toAddInTheHistory.question &&
      toAddInTheHistory.answer
    ) {
      history.unshift(toAddInTheHistory);
      setHistory(utils.clone(history));
      setToAddInTheHistory(null);
    }

    setIsStreaming(true);
    setCurrentQuestion(query);

    await _sendMessage({
      type: "ai_query",
      query: query,
    });
  };

  const insertFileTag = (file) => {
    if (!textareaElement) return;

    const text = textareaElement.value;
    const cursorPosition = textareaElement.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf("@");

    if (atIndex !== -1) {
      const newText =
        text.substring(0, atIndex) +
        `@${file.name}` +
        text.substring(cursorPosition);

      const finalText = newText.includes(`[file:${file.id}]`)
        ? newText
        : `${newText} [file:${file.id}]`;

      textareaElement.value = finalText;
      setIsQueryEmpty(finalText.trim() === "");
      setShowTagDropdown(false);

      const newCursorPosition = atIndex + file.name.length + 1;
      textareaElement.setSelectionRange(newCursorPosition, newCursorPosition);
      textareaElement.focus();
    }
  };

  const renderTagDropdown = () => {
    if (!showTagDropdown) return null;

    const filteredFiles = tagFiles.filter(
      (file) =>
        !tagFilter || file.name.toLowerCase().includes(tagFilter.toLowerCase())
    );

    return (
      <div
        className="position-absolute w-100"
        style={{ bottom: "100%", zIndex: 1050 }}
      >
        <div
          ref={fileListRef}
          className="bg-white border rounded shadow-lg"
          style={{ maxHeight: "200px", overflowY: "auto" }}
        >
          {filteredFiles.length === 0 ? (
            <div className="p-3 text-muted">
              {t("noFiles", { ns: "labels" })}
            </div>
          ) : (
            filteredFiles.map((file, index) => (
              <div
                key={file.id}
                className={`tag-item p-2 d-flex align-items-center ${
                  index === selectedIndex ? "bg-light" : ""
                }`}
                onClick={() => insertFileTag(file)}
                style={{
                  cursor: "pointer",
                  borderBottom:
                    index < filteredFiles.length - 1
                      ? "1px solid #eee"
                      : "none",
                }}
              >
                <div className="me-2" style={{ width: "16px", height: "16px" }}>
                  <IconSelector icon={file.type} />
                </div>
                <span>{file.name}</span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="chat-input-area"
      style={{
        backgroundColor: "white",
        borderTop: "1px solid #dee2e6",
        padding: "1rem",
        zIndex: 1000,
        boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
      }}
    >
      <form onSubmit={handleSubmit}>
        <div className="position-relative">
          {renderTagDropdown()}
          <div className="d-flex gap-2 align-items-end">
            <button
              type="button"
              className={`btn ${
                showHistorySidebar ? "btn-secondary" : "btn-outline-secondary"
              }`}
              onClick={() => {
                setShowHistorySidebar((prev) => !prev);
                setShowSettings(false);
                setShowFolderDocuments(false);
              }}
              title={t("chatHistory", { ns: "labels" })}
              style={{
                width: "42px",
                height: "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa-solid fa-history"></i>
            </button>
            <button
              type="button"
              className={`btn ${
                showSettings ? "btn-secondary" : "btn-outline-secondary"
              }`}
              onClick={() => {
                setShowSettings((prev) => !prev);
                setShowHistorySidebar(false);
                setShowFolderDocuments(false);
              }}
              title="Settings"
              style={{
                width: "42px",
                height: "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa-solid fa-cog"></i>
            </button>
            {isFolderFilesLoading ? (
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fa-solid fa-spinner fa-spin"></i>
              </div>
            ) : folderFiles.length > 0 ? (
              <button
                type="button"
                className={`btn ${
                  showFolderDocuments
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => {
                  setShowFolderDocuments((prev) => !prev);
                  setShowSettings(false);
                  setShowHistorySidebar(false);
                }}
                title="Documents"
                style={{
                  width: "42px",
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fa-solid fa-folder-open"></i>
              </button>
            ) : (
              <button
                type="button"
                className={`btn ${
                  showFolderDocuments
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => {
                  openEditor(fileInfo);
                }}
                title="Edit File"
                style={{
                  width: "42px",
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fa-solid fa-file-pen"></i>
              </button>
            )}
            <div className="flex-grow-1">
              <textarea
                ref={setTextareaElement}
                className="form-control"
                placeholder={t("askMe", { ns: "labels" })}
                rows="1"
                style={{
                  resize: "none",
                  height: "42px",
                  minHeight: "42px",
                  maxHeight: "120px",
                  border: "2px solid #dee2e6",
                  borderRadius: "6px",
                  lineHeight: "1.2",
                  padding: "8px 12px",
                  cursor: "text",
                }}
                onChange={(e) => setIsQueryEmpty(e.target.value.trim() === "")}
                onInput={handleTextareaInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (showTagDropdown) {
                      e.preventDefault();
                      const filteredFiles = tagFiles.filter(
                        (file) =>
                          !tagFilter ||
                          file.name
                            .toLowerCase()
                            .includes(tagFilter.toLowerCase())
                      );
                      if (filteredFiles.length > 0 && selectedIndex >= 0) {
                        insertFileTag(filteredFiles[selectedIndex]);
                      }
                    } else if (!e.shiftKey && !isQueryEmpty && !isStreaming) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  } else if (e.key === "ArrowDown") {
                    if (showTagDropdown) {
                      e.preventDefault();
                      const filteredFiles = tagFiles.filter(
                        (file) =>
                          !tagFilter ||
                          file.name
                            .toLowerCase()
                            .includes(tagFilter.toLowerCase())
                      );
                      setSelectedIndex(
                        (prev) => (prev + 1) % Math.max(filteredFiles.length, 1)
                      );
                    }
                  } else if (e.key === "ArrowUp") {
                    if (showTagDropdown) {
                      e.preventDefault();
                      const filteredFiles = tagFiles.filter(
                        (file) =>
                          !tagFilter ||
                          file.name
                            .toLowerCase()
                            .includes(tagFilter.toLowerCase())
                      );
                      setSelectedIndex(
                        (prev) =>
                          (prev - 1 + filteredFiles.length) %
                          filteredFiles.length
                      );
                    }
                  } else if (e.key === "Escape") {
                    if (showTagDropdown) {
                      e.preventDefault();
                      setShowTagDropdown(false);
                    }
                  }
                }}
                disabled={isStreaming}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isQueryEmpty || isStreaming}
              style={{
                width: "42px",
                height: "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isStreaming ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-paper-plane"></i>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
